import { useState, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { SkipToContent } from "@/components/portal/SkipToContent";
import { UserAccountMenu } from "@/components/portal/UserAccountMenu";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  getAuthSessionSnapshot,
  subscribeAuthSession,
} from "@/lib/auth/session";
import { getAbcFitnessBrandMonogramHref } from "@/lib/portal/abc-fitness-brand";
import {
  GLOFOX_API_REFERENCE_ROUTE,
  GLOFOX_DOCS_HOME_ROUTE,
} from "@/lib/portal/docs-urls";
import { GLOFOX_REQUEST_ACCESS_HREF } from "@/lib/portal/glofox-routes";
import {
  PORTAL_HOME_ROUTE,
  PORTAL_LANDING_ROUTE,
  PORTAL_SIGNUP_ROUTE,
} from "@/lib/portal/portal-routes";
import "@/styles/portal-landing.css";

type Platform = {
  id: "glofox" | "ignite" | "trainerize";
  name: string;
  version: string;
  tagline: string;
  description: string;
  accent: string;
  gradient: string;
  icon: string;
  endpoint: string;
  capabilities: Array<[icon: string, label: string]>;
  code: string;
  route?: string;
};

const PLATFORMS: Platform[] = [
  {
    id: "glofox",
    name: "Glofox",
    version: "v2.0",
    tagline: "Studio and gym management platform",
    description:
      "Class scheduling, membership billing, and member management for boutique fitness studios and gyms.",
    accent: "#00B588",
    gradient: "linear-gradient(145deg, #0a1e19, #061410)",
    icon: "ti-calendar-check",
    endpoint: "GET /v2.0/members",
    capabilities: [
      ["ti-users", "Members"],
      ["ti-calendar-check", "Bookings"],
      ["ti-credit-card", "Payments"],
      ["ti-webhook", "Webhooks"],
    ],
    code: `// List members for a location
const response = await fetch(
  "/v2.0/members?page=1&limit=50",
  { headers: {
    "Authorization": \`Bearer \${token}\`
  }}
);
const { data, has_more } = await response.json();`,
    route: "/glofox",
  },
  {
    id: "ignite",
    name: "Ignite",
    version: "v1.0",
    tagline: "Marketing automation for fitness brands",
    description:
      "Marketing automation and lead management built for multi-location fitness brands.",
    accent: "#29A9E1",
    gradient: "linear-gradient(145deg, #04152a, #020c1c)",
    icon: "ti-speakerphone",
    endpoint: "POST /v1.0/campaigns",
    capabilities: [
      ["ti-speakerphone", "Campaigns"],
      ["ti-users-group", "Segments"],
      ["ti-calendar-event", "Events"],
      ["ti-chart-bar", "Analytics"],
    ],
    code: `// Launch a campaign for a segment
const response = await fetch("/v1.0/campaigns", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ segment_id: segmentId })
});`,
  },
  {
    id: "trainerize",
    name: "Trainerize",
    version: "v3.0",
    tagline: "Coaching and workout delivery platform",
    description:
      "Workout programming, client check-ins, and nutrition tracking for personal trainers and coaches.",
    accent: "#FFCA10",
    gradient: "linear-gradient(145deg, #1c1a10, #0c0b04)",
    icon: "ti-barbell",
    endpoint: "GET /v3/clients/{id}/programs",
    capabilities: [
      ["ti-barbell", "Workouts"],
      ["ti-user", "Clients"],
      ["ti-trending-up", "Progress"],
      ["ti-carrot", "Nutrition"],
    ],
    code: `// Fetch a client's workout programs
const response = await fetch(
  \`/v3/clients/\${clientId}/programs\`,
  { headers: {
    "Authorization": \`Bearer \${token}\`
  }}
);
const { programs } = await response.json();`,
  },
];

const FEATURES = [
  {
    icon: "ti-shield-check",
    title: "Verified access",
    description:
      "Every API credential is tied to a verified business account.",
  },
  {
    icon: "ti-flask",
    title: "Sandbox environment",
    description:
      "Test against a full sandbox with realistic data before production.",
  },
  {
    icon: "ti-webhook",
    title: "Real-time webhooks",
    description: "Subscribe to member, booking, and payment events.",
  },
  {
    icon: "ti-rocket",
    title: "Fast onboarding",
    description:
      "Most developers receive sandbox credentials within two business days.",
  },
];

const ENDPOINTS = [
  ["GET", "/v2.0/members", "blue", "200 OK"],
  ["POST", "/v2.0/members", "green", "200 OK"],
  ["GET", "/v2.0/bookings", "blue", "200 OK"],
  ["POST", "/v2.0/bookings", "green", "200 OK"],
  ["DELETE", "/v2.0/bookings/{id}", "red", "204"],
  ["PATCH", "/v2.0/members/{id}", "yellow", "200 OK"],
] as const;

const TESTIMONIALS = [
  {
    business: "BeatBox Boxing Club",
    name: "James Murphy",
    role: "Founder",
    category: "Boxing Fitness Studio",
    quote:
      "ABC Glofox has helped us build a stronger community with simpler scheduling, seamless payments, and the visibility to grow.",
  },
  {
    business: "FreshFHIIT",
    name: "Colin Cannella",
    role: "Founder",
    category: "HIIT",
    quote:
      "I've been laying the foundation brick by brick for seven years, and having Glofox running quietly in the background has been a big part of that. More members, more events, more community — the business growth follows. I genuinely don't think we'd be where we are without it.",
  },
  {
    business: "Spartans Boxing, Singapore",
    name: "Nazar Musa",
    role: "Founder",
    category: "Boxing Fitness Studio",
    quote:
      "ABC Glofox has helped us scale Spartans Boxing Club with stronger support, better visibility, and a more connected franchise operation.",
  },
  {
    business: "Studio Integral",
    name: "Wal Nunes",
    role: "Founder",
    category: "Fitness Studio",
    quote:
      "ABC Glofox has helped us better understand our business, simplify payments, and deliver a more branded member experience.",
  },
] as const;

function Eyebrow({
  children,
  centered = false,
}: {
  children: React.ReactNode;
  centered?: boolean;
}) {
  return (
    <div
      className={`landing-eyebrow${centered ? " landing-eyebrow--centered" : ""}`}
    >
      <span aria-hidden />
      {children}
    </div>
  );
}

/**
 * The brand mark returns to the top of the landing. A bare `#top` href would
 * overwrite the HashRouter route instead of scrolling, so this links to the
 * landing route and scrolls on click.
 */
function BackToTopBrand({ testId }: { testId: string }) {
  return (
    <Link
      className="landing-nav-brand"
      to={PORTAL_LANDING_ROUTE}
      replace
      aria-label="ABC Fitness Developer Platform, back to top"
      onClick={() => window.scrollTo({ top: 0 })}
    >
      <AbcFitnessBrandLogo to="" theme="dark" testId={testId} />
    </Link>
  );
}

function PlatformLogo({ platform }: { platform: Platform }) {
  const monogram = getAbcFitnessBrandMonogramHref();

  return (
    <span
      className="landing-platform-logo"
      role="img"
      aria-label={`ABC ${platform.name}`}
      data-testid={`portal-landing-logo-${platform.id}`}
    >
      <span
        className="landing-platform-logo__mark"
        style={
          {
            maskImage: `url(${monogram})`,
            WebkitMaskImage: `url(${monogram})`,
          } as React.CSSProperties
        }
        aria-hidden
      />
      <span className="landing-platform-logo__word">{platform.name}</span>
    </span>
  );
}

function PlatformStatus({ platform }: { platform: Platform }) {
  if (!platform.route) {
    return (
      <span className="landing-coming-soon">
        <i className="ti ti-clock" aria-hidden />
        Documentation coming soon
      </span>
    );
  }

  return (
    <Link className="landing-platform-action" to={platform.route}>
      <span>View {platform.name} docs</span>
      <i className="ti ti-arrow-up-right" aria-hidden />
    </Link>
  );
}

function platformStyle(platform: Platform): React.CSSProperties {
  return {
    "--landing-accent": platform.accent,
    "--landing-gradient": platform.gradient,
  } as React.CSSProperties;
}

function FeaturedPlatformCard({ platform }: { platform: Platform }) {
  return (
    <article className="landing-api-card" style={platformStyle(platform)}>
      <div className="landing-card-topline">
        <PlatformLogo platform={platform} />
        <span className="landing-version">{platform.version}</span>
      </div>
      <p className="landing-tagline">{platform.tagline}</p>
      <div className="landing-capabilities">
        {platform.capabilities.map(([icon, label]) => (
          <span key={label}>
            <i className={`ti ${icon}`} aria-hidden />
            {label}
          </span>
        ))}
      </div>
      <div className="landing-code-window">
        <div className="landing-code-head">
          <span className="landing-lights" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <span>{platform.endpoint}</span>
        </div>
        <pre>{platform.code}</pre>
      </div>
      <PlatformStatus platform={platform} />
    </article>
  );
}

function PlatformCard({ platform }: { platform: Platform }) {
  return (
    <article
      className={`landing-platform-card${
        platform.route ? "" : " landing-platform-card--coming-soon"
      }`}
      style={platformStyle(platform)}
    >
      <div className="landing-platform-visual">
        <div className="landing-icon-grid">
          {platform.capabilities.map(([icon, label]) => (
            <span key={label}>
              <i className={`ti ${icon}`} aria-hidden />
              <small>{label}</small>
            </span>
          ))}
        </div>
      </div>
      <div className="landing-platform-body">
        <h3 className="landing-platform-name">
          <PlatformLogo platform={platform} />
        </h3>
        <p>{platform.description}</p>
        <div className="landing-platform-footer">
          <PlatformStatus platform={platform} />
        </div>
      </div>
    </article>
  );
}

export default function PortalLandingPage() {
  usePageTitle("Developer Platform");
  const session = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionSnapshot,
    getAuthSessionSnapshot,
  );
  const [platformIndex, setPlatformIndex] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const platform = PLATFORMS[platformIndex];
  const testimonial = TESTIMONIALS[testimonialIndex];

  const selectPrevious = () =>
    setPlatformIndex((current) => (current - 1 + PLATFORMS.length) % PLATFORMS.length);
  const selectNext = () =>
    setPlatformIndex((current) => (current + 1) % PLATFORMS.length);
  const selectPreviousQuote = () =>
    setTestimonialIndex(
      (current) => (current - 1 + TESTIMONIALS.length) % TESTIMONIALS.length,
    );
  const selectNextQuote = () =>
    setTestimonialIndex((current) => (current + 1) % TESTIMONIALS.length);
  const scrollToSection = (id: string) => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    document.getElementById(id)?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <div className="portal-landing">
      <SkipToContent />
      <nav className="landing-nav" aria-label="Primary navigation">
        <BackToTopBrand testId="portal-landing-nav-logo" />
        <div className="landing-nav-links">
          <Link
            className="landing-nav-link landing-nav-link--section"
            to={PORTAL_LANDING_ROUTE}
            onClick={() => scrollToSection("platforms")}
          >
            Platforms
          </Link>
          <Link
            className="landing-nav-link landing-nav-link--section"
            to={PORTAL_LANDING_ROUTE}
            onClick={() => scrollToSection("get-started")}
          >
            Get started
          </Link>
          {session ? (
            <>
              <Link className="landing-nav-cta" to={PORTAL_HOME_ROUTE}>
                Developer portal
              </Link>
              <UserAccountMenu
                userName={session.user.contact_name}
                testIdPrefix="portal-landing-nav"
                variant="portal"
              />
            </>
          ) : (
            <>
              <Link className="landing-nav-link" to={PORTAL_SIGNUP_ROUTE}>
                Sign up
              </Link>
              <Link className="landing-nav-cta" to={PORTAL_HOME_ROUTE}>
                Log in
              </Link>
            </>
          )}
        </div>
      </nav>

      <main id="main-content" tabIndex={-1}>
        <section className="landing-hero" id="top" data-testid="portal-landing">
          <div className="landing-hero-inner">
            <div className="landing-hero-copy">
              <Eyebrow>Developer Platform</Eyebrow>
              <h1>
                Build on the fitness stack that <em>powers</em> 40 million
                members
              </h1>
              <p>
                Production-grade REST APIs for Glofox, Ignite, and Trainerize.
                Self-service onboarding, sandbox environments, and clear
                documentation.
              </p>
              <div className="landing-hero-actions">
                {session ? (
                  <Link className="landing-primary" to={PORTAL_HOME_ROUTE}>
                    Developer portal
                  </Link>
                ) : (
                  <Link className="landing-primary" to={PORTAL_SIGNUP_ROUTE}>
                    Sign up
                  </Link>
                )}
                <Link
                  className="landing-secondary"
                  to={PORTAL_LANDING_ROUTE}
                  onClick={() => scrollToSection("platforms")}
                >
                  Explore platforms
                </Link>
              </div>
            </div>

            <div
              className="landing-carousel"
              role="region"
              aria-label="Featured platform API"
            >
              <div aria-live="polite">
                <FeaturedPlatformCard platform={platform} />
              </div>

              <div className="landing-carousel-controls">
                <button type="button" onClick={selectPrevious} aria-label="Previous platform">
                  <i className="ti ti-chevron-left" aria-hidden />
                </button>
                <div
                  className="landing-carousel-dots"
                  role="group"
                  aria-label="Choose a platform"
                >
                  {PLATFORMS.map((item, index) => (
                    <button
                      key={item.name}
                      type="button"
                      className={index === platformIndex ? "active" : ""}
                      onClick={() => setPlatformIndex(index)}
                      aria-label={`Show ${item.name}`}
                      aria-pressed={index === platformIndex}
                    />
                  ))}
                </div>
                <button type="button" onClick={selectNext} aria-label="Next platform">
                  <i className="ti ti-chevron-right" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-platforms" id="platforms">
          <div className="landing-section-heading">
            <Eyebrow centered>Platform APIs</Eyebrow>
            <h2>Tailored APIs for every fitness business type</h2>
            <p>
              Glofox, Ignite, and Trainerize each expose a dedicated REST API
              for their part of the fitness business.
            </p>
          </div>
          <div className="landing-platform-grid">
            {PLATFORMS.map((item) => (
              <PlatformCard key={item.id} platform={item} />
            ))}
          </div>
        </section>

        <section
          className="landing-testimonials"
          aria-labelledby="landing-testimonials-title"
        >
          <div className="landing-section-heading">
            <h2 id="landing-testimonials-title">What fitness businesses say</h2>
          </div>
          <div
            className="landing-quote-carousel"
            role="region"
            aria-label="Fitness business quotes"
          >
            <div aria-live="polite">
              <blockquote className="landing-testimonial" key={testimonial.business}>
                <p>{testimonial.quote}</p>
                <footer>
                  <strong>{testimonial.name}</strong>
                  <span>
                    {testimonial.role}, {testimonial.business}
                  </span>
                  <span>{testimonial.category}</span>
                </footer>
              </blockquote>
            </div>
            <div className="landing-carousel-controls landing-carousel-controls--on-light">
              <button
                type="button"
                onClick={selectPreviousQuote}
                aria-label="Previous quote"
              >
                <i className="ti ti-chevron-left" aria-hidden />
              </button>
              <div
                className="landing-carousel-dots"
                role="group"
                aria-label="Choose a quote"
              >
                {TESTIMONIALS.map((item, index) => (
                  <button
                    key={item.business}
                    type="button"
                    className={index === testimonialIndex ? "active" : ""}
                    onClick={() => setTestimonialIndex(index)}
                    aria-label={`Show quote from ${item.name}`}
                    aria-pressed={index === testimonialIndex}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={selectNextQuote}
                aria-label="Next quote"
              >
                <i className="ti ti-chevron-right" aria-hidden />
              </button>
            </div>
          </div>
        </section>

        <section className="landing-developer">
          <div className="landing-developer-copy">
            <Eyebrow>Built for developers</Eyebrow>
            <h2>APIs built for fitness at scale</h2>
            <p className="landing-lead">
              Start in a sandbox, follow versioned documentation, and move to
              production with verified access.
            </p>
            <div className="landing-feature-list">
              {FEATURES.map((feature) => (
                <div className="landing-feature" key={feature.title}>
                  <span>
                    <i className={`ti ${feature.icon}`} aria-hidden />
                  </span>
                  <div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="landing-terminal">
            <div className="landing-terminal-head">
              <span className="landing-lights" aria-hidden>
                <i />
                <i />
                <i />
              </span>
              <span>Glofox REST API — endpoint reference</span>
            </div>
            <div className="landing-endpoint-list">
              {ENDPOINTS.map(([method, path, color, status]) => (
                <div className="landing-endpoint" key={`${method}-${path}`}>
                  <span className={`landing-method landing-method--${color}`}>
                    {method}
                  </span>
                  <code>{path}</code>
                  <span className={`landing-status${status === "204" ? " landing-status--red" : ""}`}>
                    {status}
                  </span>
                </div>
              ))}
            </div>
            <Link
              className="landing-terminal-action"
              to={GLOFOX_API_REFERENCE_ROUTE}
            >
              <span>Open API Reference</span>
              <i className="ti ti-arrow-up-right" aria-hidden />
            </Link>
          </div>
        </section>

        <section
          className="landing-onboarding"
          id="get-started"
          aria-labelledby="landing-onboarding-title"
        >
          <div className="landing-section-heading">
            <Eyebrow centered>Get started</Eyebrow>
            <h2 id="landing-onboarding-title">
              Make your first API call in three steps
            </h2>
          </div>
          <ol className="landing-onboarding-steps">
            <li>
              <span aria-hidden>1</span>
              <div>
                <h3>
                  {session ? "Open your portal account" : "Create your portal account"}
                </h3>
                <p>
                  {session
                    ? "Continue with your verified portal account."
                    : "Register your business email and verify your account."}
                </p>
                <Link
                  to={session ? PORTAL_HOME_ROUTE : PORTAL_SIGNUP_ROUTE}
                >
                  {session ? "Developer portal" : "Sign up"}
                </Link>
              </div>
            </li>
            <li>
              <span aria-hidden>2</span>
              <div>
                <h3>Request API access</h3>
                <p>Tell us about your integration and required environment.</p>
                <Link to={GLOFOX_REQUEST_ACCESS_HREF}>Request API access</Link>
              </div>
            </li>
            <li>
              <span aria-hidden>3</span>
              <div>
                <h3>Build in the sandbox</h3>
                <p>Use the API Reference to make and validate your first call.</p>
                <Link to={GLOFOX_API_REFERENCE_ROUTE}>Open API Reference</Link>
              </div>
            </li>
          </ol>
        </section>

        <section className="landing-cta">
          <div>
            <Eyebrow centered>Start building today</Eyebrow>
            <h2>Ready to integrate with ABC Fitness?</h2>
            <p>
              {session
                ? "Continue to the ABC Fitness Developer Portal."
                : "Sign up for the ABC Fitness Developer Portal."}
            </p>
            <div className="landing-cta-buttons">
              {session ? (
                <Link className="landing-primary" to={PORTAL_HOME_ROUTE}>
                  Developer portal
                </Link>
              ) : (
                <>
                  <Link className="landing-primary" to={PORTAL_SIGNUP_ROUTE}>
                    Sign up
                  </Link>
                  <Link className="landing-secondary" to={PORTAL_HOME_ROUTE}>
                    Log in
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-footer" data-testid="portal-landing-footer">
        <div className="landing-footer-grid">
          <div className="landing-footer-brand">
            <BackToTopBrand testId="portal-landing-footer-logo" />
            <p>
              REST APIs for Glofox, Ignite, and Trainerize—the software behind
              40 million fitness memberships worldwide.
            </p>
          </div>
          <div className="landing-footer-nav">
            <div className="landing-footer-links">
              <h3>Platforms</h3>
              <Link to={GLOFOX_DOCS_HOME_ROUTE}>Glofox docs</Link>
              <span>Ignite — Coming soon</span>
              <span>Trainerize — Coming soon</span>
            </div>
            <div className="landing-footer-links">
              <h3>Account</h3>
              {session ? (
                <Link to={PORTAL_HOME_ROUTE}>Developer portal</Link>
              ) : (
                <>
                  <Link to={PORTAL_SIGNUP_ROUTE}>Sign up</Link>
                  <Link to={PORTAL_HOME_ROUTE}>Log in</Link>
                </>
              )}
            </div>
            <div className="landing-footer-links">
              <h3>Resources</h3>
              <a href="mailto:glofox.apisupport@abcfitness.com">Support</a>
              <a
                href="https://abcfitness.com"
                target="_blank"
                rel="noreferrer"
                aria-label="ABC Fitness website (opens in a new tab)"
              >
                abcfitness.com ↗
              </a>
            </div>
          </div>
        </div>
        <div className="landing-bottom-bar">
          <span>© 2026 ABC Fitness. All rights reserved.</span>
          <div>
            <a href="https://abcfitness.com/privacy-policy/">Privacy policy</a>
            <a href="https://abcfitness.com/terms-and-conditions/">Terms of use</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
